/**
 * Value equality for SED evidence, aware of the undefined→null coercion the
 * persistence layer applies.
 *
 * WHY THIS EXISTS. A SED round-trips through JSON, and every writer strips
 * `undefined` on the way out:
 *
 *   sedBootstrap.saveSedToGcs      — `if (v === undefined) return null`
 *   sedLoader.saveSedById          — same replacer
 *   sedAmendments.applySingleAmendmentToSed — same replacer
 *
 * So a field that is absent lives on disk as `null`, and an amendment's
 * `originalValue` — captured from the STORED slot by the SED variable browser
 * — is `null` too.
 *
 * The in-memory provider compile is the opposite. `compileSiteEvidence` builds
 * each section as one unconditional object literal (see the `gridEvidence`
 * literal in `services/siteEvidence.ts`), so an optional the provider did not
 * return is a key that is PRESENT with the value `undefined`.
 *
 * Reconciling those two representations with a naive comparison is a live
 * data-loss bug, not a nicety. `a === b` is false for `(undefined, null)`, and
 * the guard `a != null || b != null → false` that both the amendment planner
 * and the section graft used short-circuits before any deep compare. The
 * result: a refresh where the provider returned nothing for an amended field
 * was classified as "the provider moved to a THIRD value", the operator's
 * correction was discarded, and a bogus conflict was raised naming a provider
 * value of `undefined`. Fields with no producer at all (`grid.isoRegion`) hit
 * it on every single refresh.
 *
 * A `key in obj` guard does NOT rescue this: the key IS present, it just holds
 * `undefined`, so the orphan branch never fires.
 *
 * The two representations mean the same thing — "no value" — so they compare
 * equal here. Everything else falls through to a structural compare.
 */

/** True when the value carries no information: `undefined` or `null`. */
export function isEmptySedValue(v: unknown): v is null | undefined {
  return v === undefined || v === null;
}

/**
 * Structural equality that treats `undefined` and `null` as the same absent
 * value. Used by every path that has to decide whether a refreshed provider
 * value still matches the baseline an amendment was recorded against.
 *
 * NOT a general-purpose deep-equal: key ORDER matters (it compares JSON), which
 * is fine for SED payloads because both sides are built by the same compilers.
 */
export function sedValuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  // The whole point: persisted `null` and freshly-compiled `undefined` are the
  // same fact. Only when BOTH are empty — an empty vs a real value stays a
  // genuine difference.
  if (isEmptySedValue(a) || isEmptySedValue(b)) {
    return isEmptySedValue(a) && isEmptySedValue(b);
  }
  if (typeof a !== 'object' || typeof b !== 'object') return false;
  return JSON.stringify(a) === JSON.stringify(b);
}
